import React, { useState, useEffect, useMemo, useRef } from 'react';
import Container from './ui/Container';
import VideoCard from './VideoCard';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { DetailsModal } from './DiscoverySystem';
import { demoPageContent } from '../config/demoContent';

// --- STYLED COMPONENTS & HELPERS ---
const SectionHeading = ({ children, subtitle }) => (
  <div className="mb-12 text-center text-balance">
    <h2 className="text-3xl font-bold text-slate-900 mb-4">{children}</h2>
    {subtitle && <p className="text-slate-600 max-w-2xl mx-auto text-sm sm:text-base">{subtitle}</p>}
  </div>
);

const CapabilitySection = () => {
  const exportRef = useRef(null);
  const contactLinks = {
    phone: "tel:0425228778",
    email: "mailto:support@bpasolutions.au?subject=Capability%20Enquiry%20%E2%80%93%20BPA%20Solutions&body=Hello%20BPA%20Solutions%2C%0D%0AI%20am%20reviewing%20your%20capability%20statement%20and%20would%20like%20to%20discuss%20a%20potential%20requirement.%0D%0A%0D%0ARegards%2C",
    whatsapp: "https://wa.me/61425228778?text=Hello%20BPA%20Solutions%2C%20I%20am%20reviewing%20your%20capability%20statement%20and%20would%20like%20to%20discuss%20a%20requirement.",
    website: "https://www.bpasolutions.au"
  };

  const footerHtml = `
    <div style="margin-top: 60px; padding-top: 32px; border-top: 2px solid #e2e8f0; font-size: 13px; color: #475569;">
      <h3 style="font-size: 15px; font-weight: 800; color: #0f172a; margin-bottom: 16px; text-transform: uppercase; letter-spacing: 0.05em;">Contact BPA Solutions</h3>
      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 24px;">
        <div>
          <p style="margin: 0 0 10px 0;"><strong>Phone:</strong> <a href="${contactLinks.phone}" style="color: #2563eb; text-decoration: none;">0425 228 778</a></p>
          <p style="margin: 0;"><strong>Email:</strong> <a href="${contactLinks.email}" style="color: #2563eb; text-decoration: none;">support@bpasolutions.au</a></p>
        </div>
        <div>
          <p style="margin: 0 0 10px 0;"><strong>WhatsApp:</strong> <a href="${contactLinks.whatsapp}" style="color: #2563eb; text-decoration: none;">Chat with us</a></p>
          <p style="margin: 0;"><strong>Website:</strong> <a href="${contactLinks.website}" style="color: #2563eb; text-decoration: none;">www.bpasolutions.au</a></p>
        </div>
      </div>
      <p style="margin-top: 32px; font-size: 11px; color: #94a3b8; text-align: center;">&copy; ${new Date().getFullYear()} BPA Solutions. All rights reserved.</p>
    </div>
  `;

  const handleDownloadHTML = () => {
    const content = exportRef.current.innerHTML;
    const htmlTemplate = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>BPA Solutions Capability Statement</title>
        <style>
          body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; line-height: 1.6; color: #1e293b; max-width: 800px; margin: 40px auto; padding: 20px; background: #f8fafc; }
          .container { background: white; padding: 60px; border-radius: 24px; border: 1px solid #e2e8f0; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1); }
          h1 { color: #0f172a; font-size: 32px; margin-bottom: 8px; border-bottom: 4px solid #2563eb; display: inline-block; }
          h2 { color: #0f172a; font-size: 24px; margin-top: 40px; margin-bottom: 16px; font-weight: 800; }
          h2.subtitle { color: #64748b; font-size: 18px; margin-top: 0; margin-bottom: 32px; font-weight: 400; }
          p { margin-bottom: 16px; }
          ul, ol { margin-bottom: 24px; padding-left: 20px; }
          li { margin-bottom: 8px; }
        </style>
      </head>
      <body>
        <div class="container">${content}${footerHtml}</div>
      </body>
      </html>
    `;
    const blob = new Blob([htmlTemplate], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bpa-solutions-capability-statement.html';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportPDF = () => {
    window.print();
  };

  return (
    <section id="capability" className="py-20 bg-white scroll-mt-24 border-t border-slate-100">
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #capability-content, #capability-content * { visibility: visible; }
          #capability-content { 
            position: absolute; left: 0; top: 0; width: 100%; padding: 40px; color: #000 !important; font-family: serif;
          }
          .no-print { display: none !important; }
          h1 { font-size: 28pt; border-bottom: 2pt solid #2563eb; }
          h2 { font-size: 18pt; margin-top: 25pt; page-break-after: avoid; }
          p, li { font-size: 11pt; line-height: 1.5; }
          .print-footer { 
            display: block !important; position: fixed; bottom: 0; width: 100%; 
            font-size: 9pt; color: #666; padding-top: 10pt; border-top: 1px solid #eee; 
            text-align: left;
          }
          .print-header { display: block !important; font-weight: bold; font-size: 10pt; margin-bottom: 20pt; }
        }
      `}</style>
      
      <Container>
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 no-print">
          <div className="max-w-2xl">
            <h2 className="text-3xl font-bold text-slate-900 mb-2">Capability Statement</h2>
            <p className="text-slate-600">A detailed overview of our data analysis and automation capabilities for internal reference and due diligence.</p>
          </div>
          <div className="flex flex-col items-end gap-3">
            <div className="flex gap-3">
              <button 
                onClick={handleDownloadHTML}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-bold text-slate-700 hover:bg-slate-50 transition-colors shadow-sm"
              >
                <SafeIcon icon={FiIcons.FiDownload} className="w-4 h-4" /> Download HTML
              </button>
              <button 
                onClick={handleExportPDF}
                className="flex items-center gap-2 px-4 py-2 bg-slate-900 border border-slate-900 rounded-lg text-sm font-bold text-white hover:bg-slate-800 transition-colors shadow-sm"
              >
                <SafeIcon icon={FiIcons.FiFileText} className="w-4 h-4" /> Export PDF
              </button>
            </div>
            <span className="text-[10px] text-slate-400 font-medium italic">PDF export uses your browser print dialog. Choose "Save as PDF".</span>
          </div>
        </div>

        <div id="capability-content" className="prose prose-slate max-w-4xl mx-auto bg-slate-50/50 rounded-[2rem] p-8 md:p-16 border border-slate-200">
          <div className="hidden print-header">BPA Solutions – Capability Statement</div>
          
          <div ref={exportRef}>
            <h1 className="text-4xl font-extrabold text-slate-900 mb-2">BPA Solutions Capability Statement</h1>
            <h2 className="subtitle text-xl text-slate-500 font-medium mb-12">Data, Dashboards, and Business Process Automation for Australian Businesses</h2>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-4">Overview</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              BPA Solutions works with Australian businesses and organisations to transform raw data into clear insights, structured reporting, and decision-ready dashboards. We specialise in analysing existing data sources such as Excel files, CSV exports, and structured PDFs, then delivering outputs that support operational, financial, compliance, and management decision-making.
            </p>
            <p className="text-slate-700 leading-relaxed mb-8">
              Our work is consultative and tailored. The examples shown across this site represent capability demonstrations rather than fixed products or off-the-shelf software.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-4">What We Do</h2>
            <p className="text-slate-700 mb-4">BPA Solutions provides end-to-end support across data analysis, dashboard delivery, and workflow automation, including:</p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 mb-8">
              <li>Data analysis and structuring from Excel, CSV, and PDF sources</li>
              <li>Design and delivery of dashboards for operational and executive review</li>
              <li>Automation of repetitive reporting and document-based workflows</li>
              <li>Conversion of spreadsheets into visual reporting and prioritisation tools</li>
              <li>Decision-support reporting for internal review and governance</li>
              <li>Website delivery with integrated automation and data workflows</li>
            </ul>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-4">Problems We Commonly Solve</h2>
            <p className="text-slate-700 mb-4">Australian businesses typically engage BPA Solutions when they face challenges such as:</p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 mb-8">
              <li>Multiple spreadsheets with no single source of truth</li>
              <li>Manual reconciliation and reporting that consumes time and introduces errors</li>
              <li>Poor visibility over performance, risk, incidents, or financial trends</li>
              <li>Difficulty explaining data clearly to management or stakeholders</li>
              <li>Compliance or internal review processes relying on fragmented information</li>
              <li>Growing businesses outgrowing ad hoc Excel-based processes</li>
            </ul>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-4">How We Work</h2>
            <p className="text-slate-700 mb-4">Our typical engagement approach follows a structured, repeatable process:</p>
            <ol className="list-decimal pl-6 space-y-2 text-slate-700 mb-8">
              <li>Review existing data and business objectives</li>
              <li>Analyse, clean, and structure data into usable formats</li>
              <li>Deliver dashboards, reports, or automated workflows</li>
              <li>Provide clear explanation of outputs and key insights</li>
              <li>Support decision-making through practical, usable information</li>
            </ol>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-4">Example Use Cases</h2>
            <p className="text-slate-700 mb-4">Our capability is commonly applied across scenarios such as:</p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 mb-8">
              <li>Risk registers transformed into prioritised visual heatmaps</li>
              <li>Incident reporting consolidated into trend and severity dashboards</li>
              <li>Bank statements extracted and structured for financial analysis</li>
              <li>Commission and hours data analysed to identify discrepancies</li>
              <li>Personal and business spending categorised for clearer oversight</li>
              <li>Bulk document generation such as certificates or reports</li>
              <li>Operational data visualised for management and board discussions</li>
            </ul>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-4">Industries We Support</h2>
            <p className="text-slate-700 mb-4">BPA Solutions supports a broad range of Australian industries, including:</p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 mb-8">
              <li>Trades and field-based businesses</li>
              <li>Professional and consulting services</li>
              <li>Marketing and advertising agencies</li>
              <li>Financial and advisory services</li>
              <li>Training and education providers</li>
              <li>Property and facilities-related organisations</li>
              <li>Government departments and agencies</li>
            </ul>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-4">What Clients Value</h2>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 mb-8">
              <li>Clear, structured outputs that replace guesswork with evidence</li>
              <li>Dashboards and reports that are easy to interpret and explain</li>
              <li>A focus on outcomes and decision support rather than tools</li>
              <li>Professional delivery and attention to detail</li>
              <li>Practical guidance aligned with Australian business practices</li>
            </ul>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-4">Delivery Model</h2>
            <p className="text-slate-700 mb-8">
              BPA Solutions operates as a service-led consultancy. Dashboards and automation are used as delivery mechanisms for analysis and insight, not sold as standalone software products. Interactive access may be provided where appropriate to support clarity and ongoing use.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-4">Next Steps</h2>
            <p className="text-slate-700 mb-8 text-balance">
              If your requirement is not represented in the examples shown, we review each request individually. Use the enquiry form at www.bpasolutions.au to describe your objectives.
            </p>
          </div>

          <div className="hidden print-footer">
            <div className="flex justify-between border-t border-slate-200 pt-4 mt-8">
              <div className="space-y-1">
                <p>Phone: 0425 228 778</p>
                <p>Email: support@bpasolutions.au</p>
              </div>
              <div className="space-y-1 text-right">
                <p>WhatsApp: 0425 228 778</p>
                <p>Website: www.bpasolutions.au</p>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
};

const SEOLinks = () => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-20 no-print">
    <a href="https://trades.bpasolutions.au" className="group p-8 bg-slate-50 border border-slate-200 rounded-2xl hover:border-blue-500 hover:bg-white transition-all">
      <h3 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-blue-600">Trades Solutions Hub</h3>
      <p className="text-sm text-slate-600 mb-4">Specialised automation and reporting for trades businesses across NSW, Victoria, and Queensland.</p>
      <span className="text-xs font-bold text-blue-600 uppercase tracking-widest flex items-center gap-2">Explore Trades Capability <SafeIcon icon={FiIcons.FiArrowRight} /></span>
    </a>
    <a href="https://services.bpasolutions.au" className="group p-8 bg-slate-50 border border-slate-200 rounded-2xl hover:border-blue-500 hover:bg-white transition-all">
      <h3 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-blue-600">Professional Services Hub</h3>
      <p className="text-sm text-slate-600 mb-4">Data analysis and custom dashboards for consulting, finance, and agency teams Australia-wide.</p>
      <span className="text-xs font-bold text-blue-600 uppercase tracking-widest flex items-center gap-2">Explore Services Capability <SafeIcon icon={FiIcons.FiArrowRight} /></span>
    </a>
  </div>
);

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const faqs = [
    { q: "How does BPA Solutions work with businesses in NSW?", a: "We support businesses throughout NSW, including Sydney and Western Sydney, by automating data extraction from local suppliers and creating dashboards for regional operations." },
    { q: "What industries do you support in Victoria?", a: "Our examples cover professional services in Melbourne and Geelong, specifically management consulting and training providers needing structured reporting." },
    { q: "Can you help Queensland organisations with data?", a: "Yes. Our representative scenarios include work for Queensland Government departments and Brisbane-based retail agencies requiring visual analytics." },
    { q: "Are these solutions off-the-shelf products?", a: "No. These are examples of capability. Every solution is a custom engagement designed to meet the specific data structure and goals of the organisation." },
    { q: "How do these dashboards support better business decisions?", a: "By consolidating and structuring raw data into clear visual outputs, dashboards highlight trends, risks, and exceptions that support informed decision-making." },
    { q: "Are these outputs suitable for management or board-level review?", a: "Yes. Many examples are designed specifically to support internal reviews, management discussions, and governance reporting." },
    { q: "Do you help interpret the outputs or just deliver them?", a: "Yes. Outputs are delivered with clear explanations so stakeholders understand what the information means and how it can be used." },
    { q: "Can these examples support compliance or audit discussions?", a: "In some cases, structured reporting and dashboards are used to support internal compliance reviews or audit preparation, depending on scope." },
    { q: "What happens if our data quality is poor?", a: "Part of the process involves identifying data gaps or inconsistencies so they can be addressed before decisions are made." },
    { q: "Can these examples be adapted as our business changes?", a: "Yes. Capability examples can be adjusted over time as reporting needs and business priorities evolve." }
  ];

  return (
    <div className="mb-20 no-print">
      <div className="mb-8 p-6 bg-blue-50/50 border border-blue-100 rounded-2xl text-center">
        <p className="text-sm text-slate-700 font-medium italic">
          "These examples demonstrate how BPA Solutions supports Australian businesses and organisations across NSW, Victoria, and Queensland with data analysis, dashboards, and automation. The questions below explain how these capabilities are typically applied."
        </p>
      </div>
      <div className="space-y-3 max-w-3xl mx-auto">
        {faqs.map((faq, i) => (
          <div key={i} className="border border-slate-200 rounded-xl overflow-hidden bg-white">
            <button 
              onClick={() => setOpenIndex(openIndex === i ? null : i)}
              className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-slate-50 transition-colors"
            >
              <span className="font-bold text-slate-900 text-sm sm:text-base">{faq.q}</span>
              <SafeIcon icon={openIndex === i ? FiIcons.FiChevronUp : FiIcons.FiChevronDown} className="text-slate-400 shrink-0 ml-4" />
            </button>
            <AnimatePresence>
              {openIndex === i && (
                <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
                  <div className="px-6 pb-4 text-slate-600 text-sm leading-relaxed border-t border-slate-50 pt-4">{faq.a}</div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}
      </div>
    </div>
  );
};

const UserStories = () => {
  const stories = [
    { title: "Locksmith business, Parramatta NSW", name: "Jason McAllister", problem: "The business was managing call-outs, invoices, and technician hours across disconnected spreadsheets, making it difficult to see profitability and unpaid work.", action: "BPA Solutions consolidated the raw data into a structured dashboard, aligning job data, payments, and hours worked.", outcome: "The business gained a clear view of unpaid jobs, revenue trends, and cost leakage.", impact: "This allowed the owner to prioritise follow-ups, adjust pricing on certain services, and make informed staffing decisions." },
    { title: "Management consulting firm, Melbourne VIC", name: "Emily Cartwright", problem: "Billable hours and commission calculations were being reviewed manually, leading to inconsistencies and delayed internal discussions.", action: "BPA Solutions analysed the firm's time and commission data and produced a clear reporting view highlighting variances.", outcome: "Discrepancies were identified quickly and reporting became consistent across reporting periods.", impact: "Management could make evidence-based decisions around resourcing, commission structures, and performance reviews." },
    { title: "Queensland Government department, Brisbane QLD", name: "Rebecca Hayes", problem: "Incident records were spread across multiple systems with no clear prioritisation or trend visibility.", action: "BPA Solutions consolidated the data and delivered a visual incident dashboard highlighting severity and recurrence.", outcome: "High-risk areas and recurring issues became immediately visible.", impact: "This supported more effective prioritisation, follow-up planning, and internal reporting." },
    { title: "Marketing agency, Brisbane QLD", name: "Daniel Wong", problem: "A marketing agency struggled to track campaign spend and outcomes across clients, leading to reporting delays and client frustration.", action: "BPA Solutions transformed the raw spend data into a consolidated reporting view with performance benchmarks.", outcome: "The agency achieved 100% visibility over cross-channel performance in a single view.", impact: "This enabled the team to reallocate budget to high-performing campaigns in real-time and provide data-backed advice to clients." },
    { title: "Landscaping company, Penrith NSW", name: "Matthew Collins", problem: "A landscaping company needed better visibility over job costs and margins to understand why certain projects were over-budget.", action: "BPA Solutions structured their data into a dashboard that highlighted cost overruns and profitability by project type.", outcome: "Margin leakage was identified in specific project categories that were previously assumed to be profitable.", impact: "The business adjusted their quoting strategy for complex jobs, leading to improved overall profitability." },
    { title: "Financial services firm, Sydney NSW", name: "Priya Nair" }
  ];

  const fullStories = [...stories, ...stories, ...stories].slice(0, 15);

  return (
    <div className="mb-32 no-print">
      <SectionHeading subtitle="Examples below are representative scenarios used to demonstrate capability. They do not reference specific client engagements.">
        How businesses and organisations apply these solutions
      </SectionHeading>
      <div className="relative h-[600px] overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/30">
        <motion.div 
          animate={{ y: [0, -2800] }} 
          transition={{ duration: 75, repeat: Infinity, ease: "linear" }}
          whileHover={{ animationPlayState: 'paused' }}
          className="p-8 space-y-8"
        >
          {fullStories.map((s, i) => (
            <div key={i} className="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-6 border-b border-slate-50 pb-4">
                <div className="text-blue-600 text-[10px] font-bold uppercase tracking-widest">{s.title}</div>
                <div className="font-bold text-slate-900 text-sm">{s.name}</div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter block mb-1">Problem</span>
                    <p className="text-slate-600 text-sm leading-relaxed italic">"{s.problem}"</p>
                  </div>
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter block mb-1">Action</span>
                    <p className="text-slate-700 text-sm leading-relaxed">{s.action}</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter block mb-1">Outcome</span>
                    <p className="text-slate-700 text-sm leading-relaxed font-medium">{s.outcome}</p>
                  </div>
                  <div className="p-3 bg-blue-50/50 rounded-lg border border-blue-100">
                    <span className="text-[10px] font-bold text-blue-600 uppercase tracking-tighter block mb-1">Decision Impact</span>
                    <p className="text-slate-800 text-sm leading-relaxed font-semibold">{s.impact}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </motion.div>
        <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-white to-transparent pointer-events-none" />
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-white to-transparent pointer-events-none" />
      </div>
    </div>
  );
};

const ReviewsSection = () => {
  const reviews = [
    { name: "Michael Thompson", loc: "Landscaping company, NSW", text: "BPA Solutions helped us move from scattered spreadsheets to a clear reporting view that actually made sense. The analysis highlighted issues we were previously guessing at. Having structured dashboards meant decisions were based on facts rather than assumptions." },
    { name: "Sarah O'Connell", loc: "Management consulting firm, VIC", text: "We needed clarity around performance and reporting, not another complicated system. BPA Solutions delivered clean analysis that supported internal discussions and planning. The outputs were easy to understand and genuinely useful for decision-making." },
    { name: "Daniel Reyes", loc: "Locksmith business, QLD", text: "Our data was technically available, but it was not telling us anything useful. BPA Solutions structured the information into reports that clearly showed where attention was needed. This made internal reviews faster and more objective." },
    { name: "Natalie Harper", loc: "Marketing agency, NSW", text: "The dashboards helped us see trends and risks that were not obvious before. This supported better prioritisation and clearer communication internally. The focus was always on outcomes, not unnecessary complexity." },
    { name: "Andrew Collins", loc: "Financial services firm, VIC", text: "Moving away from manual data entry was a massive relief for our team. The automated reporting tools delivered by BPA Solutions provided a level of detail we simply couldn't achieve before. Decisions are now much easier to justify." }
  ];

  const fullReviews = [...reviews, ...reviews, ...reviews, ...reviews, ...reviews, ...reviews].slice(0, 30);

  return (
    <div className="mb-32 overflow-hidden bg-slate-900 py-24 -mx-4 sm:-mx-8 lg:-mx-12 px-4 sm:px-8 lg:px-12 no-print">
      <div className="text-center mb-16 px-4">
        <h2 className="text-3xl font-bold text-white mb-4">What clients value about working with BPA Solutions</h2>
        <p className="text-slate-400 max-w-2xl mx-auto">Feedback below reflects common themes across analysis, dashboard, and automation engagements.</p>
      </div>
      <div className="relative flex">
        <motion.div 
          animate={{ x: [0, -4500] }} 
          transition={{ duration: 120, repeat: Infinity, ease: "linear" }}
          whileHover={{ animationPlayState: 'paused' }}
          className="flex gap-6"
        >
          {fullReviews.map((r, i) => (
            <div key={i} className="w-[450px] shrink-0 bg-slate-800/50 border border-slate-700 p-8 rounded-2xl flex flex-col">
              <div className="flex text-amber-400 mb-4">
                {Array(5).fill(null).map((_, j) => <SafeIcon key={j} icon={FiIcons.FiStar} className="w-4 h-4 fill-current" />)}
              </div>
              <p className="text-slate-300 text-sm mb-8 leading-relaxed italic flex-grow">"{r.text}"</p>
              <div className="border-t border-slate-700 pt-4">
                <div className="font-bold text-white text-sm">{r.name}</div>
                <div className="text-slate-500 text-xs mt-1">{r.loc}</div>
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </div>
  );
};

const CustomRequestSection = () => {
  const [status, setStatus] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [formData, setFormData] = useState({
    goal: '',
    dataTypes: [],
    outputTypes: [],
    frequency: 'Not sure yet',
    context: '',
    name: '',
    email: '',
    phone: ''
  });

  const BUSINESS_NAME = "BPA Solutions";
  const LEAD_EMAIL = "anish.m.malhotra@gmail.com";
  const EMAILIT_API_KEY = import.meta.env.VITE_EMAILIT_API_KEY;
  const EMAILIT_ENDPOINT = "https://api.emailit.com/v1/emails";

  const dataOptions = ['Excel / CSV', 'PDF documents', 'Database export', 'Mixed or not sure'];
  const outputOptions = ['Interactive dashboard', 'PDF report', 'Excel output', 'Visual summary', 'Not sure (advise me)'];
  const frequencyOptions = ['One-off analysis', 'Ongoing / recurring', 'Not sure yet'];

  const toggleSelection = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value) 
        ? prev[field].filter(i => i !== value) 
        : [...prev[field], value]
    }));
  };

  const generateBrandedHtml = (subtitle, source, fields, isUser = false) => {
    const brandBlue = "#2563eb";
    const bgDark = "#0B1220";
    const bgCard = "#101B33";
    const textMain = "#f8fafc";
    const textDim = "#94a3b8";

    return `
      <div style="background-color: ${bgDark}; font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 40px 20px; color: ${textMain};">
        <div style="max-width: 600px; margin: 0 auto; background-color: ${bgCard}; border: 1px solid #1e293b; border-radius: 24px; overflow: hidden; box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);">
          <div style="padding: 40px;">
            <div style="margin-bottom: 32px;">
              <h1 style="font-size: 24px; font-weight: 800; margin: 0; color: ${textMain}; display: inline-block; border-bottom: 3px solid ${brandBlue}; padding-bottom: 4px;">${BUSINESS_NAME}</h1>
              <p style="font-size: 16px; color: ${textDim}; margin: 12px 0 0 0;">${subtitle}</p>
            </div>
            <table style="width: 100%; border-collapse: collapse;">
              ${Object.entries(fields).map(([key, val]) => `
                <tr>
                  <td style="padding: 14px 0; border-bottom: 1px solid #1e293b; font-size: 13px; font-weight: 700; color: ${textDim}; width: 35%; vertical-align: top;">${key}</td>
                  <td style="padding: 14px 0; border-bottom: 1px solid #1e293b; font-size: 13px; color: ${textMain}; line-height: 1.5;">${Array.isArray(val) ? val.join(', ') : val || '—'}</td>
                </tr>
              `).join('')}
            </table>
          </div>
        </div>
      </div>
    `;
  };

  const sendEmail = async (to, from, subject, html, text) => {
    if (!EMAILIT_API_KEY) throw new Error("Email configuration is missing.");
    try {
      const res = await fetch(EMAILIT_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${EMAILIT_API_KEY}` },
        body: JSON.stringify({ to, from, subject, html, text })
      });
      if (!res.ok) throw new Error("Email server error.");
      return true;
    } catch (err) { throw err; }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.goal || formData.dataTypes.length === 0 || formData.outputTypes.length === 0 || !formData.name || !formData.email) {
      setErrorMessage("Please complete all required fields.");
      setStatus('error');
      return;
    }

    setStatus('sending');
    const source = { title: "BPA Solutions Demo", url: window.location.href };
    const payloadFields = {
      "Achieve Goal": formData.goal,
      "Data Types": formData.dataTypes,
      "Output Types": formData.outputTypes,
      "Is this a one-off or ongoing need?": formData.frequency,
      "Additional Context": formData.context,
      "Name": formData.name,
      "Email": formData.email,
      "Phone/WhatsApp": formData.phone || "Not provided"
    };

    try {
      await sendEmail(LEAD_EMAIL, `Alerts@bpasolutions.au`, "New Demo Enquiry – BPA Solutions", generateBrandedHtml("New intake form received", source, payloadFields, false), "New enquiry from website.");
      setStatus('success');
    } catch (err) {
      setErrorMessage(err.message);
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-slate-900 rounded-[2.5rem] p-12 text-center text-white border border-slate-800 shadow-2xl no-print">
        <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6 text-emerald-400">
          <SafeIcon icon={FiIcons.FiCheckCircle} className="w-10 h-10" />
        </div>
        <h3 className="text-3xl font-bold mb-4">Request sent</h3>
        <p className="text-slate-400 max-w-md mx-auto mb-8">Thanks. We’ll review your request and get back to you with the best approach for your specific data needs.</p>
        <button onClick={() => setStatus('idle')} className="text-sm font-bold text-slate-500 hover:text-white transition-colors">Submit another request</button>
      </motion.div>
    );
  }

  return (
    <div id="enquiry-form" className="bg-slate-50 rounded-[2.5rem] p-8 md:p-16 border border-slate-200 no-print scroll-mt-24">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">Need a custom solution?</h2>
          <p className="text-slate-600">Provide detail below and we’ll advise on the best approach for your data requirements.</p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-10">
          <div className="space-y-4">
            <label htmlFor="goal" className="block text-sm font-bold text-slate-900 uppercase tracking-widest">What are you trying to achieve? <span className="text-blue-600">*</span></label>
            <textarea 
              id="goal" 
              required 
              className="w-full bg-white border border-slate-200 rounded-2xl p-5 focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all min-h-[120px] shadow-sm" 
              placeholder="Describe the problem or outcome you’re looking for." 
              value={formData.goal} 
              onChange={e => setFormData({ ...formData, goal: e.target.value })} 
            />
          </div>

          <div className="space-y-4">
            <label className="block text-sm font-bold text-slate-900 uppercase tracking-widest">What type of data do you have? <span className="text-blue-600">*</span></label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {dataOptions.map(opt => (
                <button 
                  key={opt} 
                  type="button" 
                  onClick={() => toggleSelection('dataTypes', opt)} 
                  className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all text-left ${formData.dataTypes.includes(opt) ? 'bg-blue-50 border-blue-500 text-blue-700' : 'bg-white border-slate-100 text-slate-600 hover:border-slate-200'}`}
                >
                  <div className={`w-5 h-5 rounded border flex items-center justify-center ${formData.dataTypes.includes(opt) ? 'bg-blue-500 border-blue-500 text-white' : 'border-slate-300 bg-white'}`}>
                    {formData.dataTypes.includes(opt) && <SafeIcon icon={FiIcons.FiCheck} className="w-3 h-3" />}
                  </div>
                  <span className="text-sm font-medium">{opt}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <label className="block text-sm font-bold text-slate-900 uppercase tracking-widest">What kind of output do you expect? <span className="text-blue-600">*</span></label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {outputOptions.map(opt => (
                <button 
                  key={opt} 
                  type="button" 
                  onClick={() => toggleSelection('outputTypes', opt)} 
                  className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all text-left ${formData.outputTypes.includes(opt) ? 'bg-blue-50 border-blue-500 text-blue-700' : 'bg-white border-slate-100 text-slate-600 hover:border-slate-200'}`}
                >
                  <div className={`w-5 h-5 rounded border flex items-center justify-center ${formData.outputTypes.includes(opt) ? 'bg-blue-500 border-blue-500 text-white' : 'border-slate-300 bg-white'}`}>
                    {formData.outputTypes.includes(opt) && <SafeIcon icon={FiIcons.FiCheck} className="w-3 h-3" />}
                  </div>
                  <span className="text-sm font-medium">{opt}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <label className="block text-sm font-bold text-slate-900 uppercase tracking-widest">Is this a one-off or ongoing need?</label>
            <div className="flex flex-wrap gap-3">
              {frequencyOptions.map(opt => (
                <button 
                  key={opt} 
                  type="button" 
                  onClick={() => setFormData({ ...formData, frequency: opt })} 
                  className={`flex items-center gap-3 px-6 py-3 rounded-xl border-2 transition-all ${formData.frequency === opt ? 'bg-slate-900 border-slate-900 text-white' : 'bg-white border-slate-100 text-slate-600 hover:border-slate-200'}`}
                >
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${formData.frequency === opt ? 'border-white' : 'border-slate-300'}`}>
                    {formData.frequency === opt && <div className="w-2 h-2 bg-white rounded-full" />}
                  </div>
                  <span className="text-sm font-medium">{opt}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <label htmlFor="context" className="block text-sm font-bold text-slate-900 uppercase tracking-widest">Additional context</label>
            <textarea 
              id="context" 
              className="w-full bg-white border border-slate-200 rounded-2xl p-5 focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all min-h-[100px] shadow-sm" 
              placeholder="Anything else that might be relevant." 
              value={formData.context} 
              onChange={e => setFormData({ ...formData, context: e.target.value })} 
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-10 border-t border-slate-200">
            <div className="space-y-2">
              <label htmlFor="name" className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-1">Name <span className="text-blue-600">*</span></label>
              <input id="name" required type="text" className="w-full bg-white border border-slate-200 rounded-xl p-4 text-sm focus:border-blue-500 outline-none shadow-sm transition-all" placeholder="Your name" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} />
            </div>
            <div className="space-y-2">
              <label htmlFor="email" className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-1">Email <span className="text-blue-600">*</span></label>
              <input id="email" required type="email" className="w-full bg-white border border-slate-200 rounded-xl p-4 text-sm focus:border-blue-500 outline-none shadow-sm transition-all" placeholder="your@email.com" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} />
            </div>
            <div className="space-y-2">
              <label htmlFor="phone" className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-1">Phone / WhatsApp</label>
              <input id="phone" type="text" className="w-full bg-white border border-slate-200 rounded-xl p-4 text-sm focus:border-blue-500 outline-none shadow-sm transition-all" placeholder="Best number" value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} />
            </div>
          </div>

          {status === 'error' && (
            <div className="p-4 bg-red-50 border border-red-200 text-red-600 rounded-xl text-sm font-medium flex items-center gap-3">
              <SafeIcon icon={FiIcons.FiAlertCircle} className="w-5 h-5 shrink-0" /> {errorMessage}
            </div>
          )}

          <button 
            type="submit" 
            disabled={status === 'sending'} 
            className="w-full bg-slate-900 text-white rounded-2xl py-6 font-bold text-lg hover:bg-slate-800 transition-all shadow-xl hover:shadow-2xl flex items-center justify-center gap-3 disabled:opacity-50"
          >
            {status === 'sending' ? (
              <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <><SafeIcon icon={FiIcons.FiSend} className="w-5 h-5" /> Request Review</>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default function DemoSection() {
  const [query, setQuery] = useState('');
  const [activeChip, setActiveChip] = useState('All');
  const [shortlist, setShortlist] = useState(() => JSON.parse(localStorage.getItem('demoShortlist') || '[]'));
  const [selectedDemo, setSelectedDemo] = useState(null);
  const sectionRef = useRef(null);

  const chips = ['All', 'Live Apps', 'Smart Forms', 'Dashboards', 'Reports', 'Excel', 'PDF', 'Verification', 'Shortlisted'];
  const allDemos = useMemo(() => demoPageContent.categories.flatMap(c => c.demos), []);

  useEffect(() => {
    localStorage.setItem('demoShortlist', JSON.stringify(shortlist));
  }, [shortlist]);

  const toggleShortlist = (id) => {
    setShortlist(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const filteredDemos = useMemo(() => {
    let list = allDemos;
    
    if (activeChip === 'Shortlisted') {
      list = list.filter(d => shortlist.includes(d.id));
    } else if (activeChip !== 'All') {
      list = list.filter(demo => {
        const text = `${demo.title} ${demo.description} ${demo.category} ${(demo.tags || []).join(' ')} ${(demo.deliverables || []).join(' ')} ${(demo.keywords || []).join(' ')} ${(demo.bullets || []).join(' ')}`.toLowerCase();
        if (activeChip === 'Live Apps') return /live demo|app|tool|showcase|open demo/.test(text);
        if (activeChip === 'Smart Forms') return /smart forms|form library|multiple forms|intake|consent|waiver|booking|enquiry|service request|digital signature/.test(text);
        if (activeChip === 'Dashboards') return text.includes('dashboard');
        if (activeChip === 'Reports') return text.includes('report');
        if (activeChip === 'Excel') return text.includes('excel') || text.includes('csv');
        if (activeChip === 'PDF') return text.includes('pdf');
        if (activeChip === 'Verification') return /verification|email verification|sms verification|signature|verified forms/.test(text);
        return text.includes(activeChip.toLowerCase());
      });
    }

    if (query.trim()) {
      const tokens = query.toLowerCase().split(' ').filter(t => t.length > 0);
      list = list.filter(demo => {
        const searchable = `${demo.title} ${demo.description} ${demo.category} ${(demo.tags || []).join(' ')} ${(demo.deliverables || []).join(' ')} ${(demo.keywords || []).join(' ')} ${(demo.bullets || []).join(' ')}`.toLowerCase();
        return tokens.every(token => searchable.includes(token));
      });
    } 
    return list;
  }, [allDemos, query, activeChip, shortlist]);

  return (
    <section ref={sectionRef} id="demos" className="py-20 border-b border-slate-100 scroll-mt-24 bg-white">
      <Container className="mb-12 no-print">
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-2">
            <label htmlFor="demo-search-input" className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-1">Search demos</label>
            <div className="flex items-center gap-4">
              <div className="relative flex-1 group">
                <SafeIcon icon={FiIcons.FiSearch} className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
                <input id="demo-search-input" type="text" placeholder="Search outcome or deliverable..." className="w-full pl-14 pr-36 py-5 bg-slate-50 border-2 border-slate-100 rounded-2xl focus:bg-white outline-none transition-all text-slate-900 text-lg shadow-sm" value={query} onChange={(e) => setQuery(e.target.value)} />
                <div className="absolute right-5 top-1/2 -translate-y-1/2">
                  <span className="text-[11px] font-bold text-slate-500 px-3 py-1.5 bg-white border border-slate-200 rounded-lg uppercase tracking-wider shadow-sm">Showing {filteredDemos.length}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 overflow-x-auto pb-2 no-scrollbar">
            {chips.map(chip => (
              <button key={chip} onClick={() => setActiveChip(chip)} className={`px-4 py-2 rounded-full text-[11px] font-bold border transition-all shrink-0 ${activeChip === chip ? 'bg-slate-900 border-slate-900 text-white shadow-lg' : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300'}`}>
                {chip}
              </button>
            ))}
          </div>
        </div>
      </Container>

      <Container>
        <div className="space-y-32">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 no-print">
            <AnimatePresence mode="popLayout">
              {filteredDemos.length > 0 ? filteredDemos.map(demo => <VideoCard key={demo.id} demo={demo} onOpenModal={setSelectedDemo} isShortlisted={shortlist.includes(demo.id)} onShortlist={toggleShortlist} />) : <div className="col-span-full py-20 text-center text-slate-400">No matching demos found.</div>}
            </AnimatePresence>
          </div>

          <SEOLinks />
          <FAQSection />
          <UserStories />
          <ReviewsSection />
          <CapabilitySection />
          <CustomRequestSection />
        </div>
      </Container>

      <DetailsModal isOpen={!!selectedDemo} demo={selectedDemo} onClose={() => setSelectedDemo(null)} isShortlisted={selectedDemo && shortlist.includes(selectedDemo.id)} onShortlist={toggleShortlist} />
    </section>
  );
}